/**
 * The other half of `omitNullish()`'s fix (see that file's doc comment): `omitNullish()` solved
 * CREATE forms sending `null` for a blank optional field. Adopting it on EDIT forms introduced a
 * documented, known side effect (`docs/FORM_SWEEP_RESULTS.md`, "Known real gaps" #4): once a
 * client always omits blank fields, the server has no way left to tell "the user left this field
 * alone" apart from "the user actively cleared it back to blank" - omitting a key meant BOTH, and
 * there was no way to intentionally un-set a previously-set value through any edit form.
 *
 * THE CONVENTION (apply this everywhere a form edits an existing record with optional fields -
 * this file is the canonical reference, link back here from any schema or form that uses it):
 *
 *  - Wire format, three distinct states per optional field on a PATCH/update body:
 *      1. Key is absent entirely        -> "don't touch this field", leave the stored value as is.
 *      2. Key is present, value `null`  -> "clear this field", explicitly un-set it back to blank.
 *      3. Key is present, a real value  -> "set this field to this value".
 *    `undefined` is never sent on the wire (JSON has no `undefined`) - only (1) and (2)/(3) exist
 *    once serialized, which is exactly why `null` is available as the distinct "clear" signal.
 *
 *  - Server (zod): every update/PATCH schema field that can legitimately be blanked back out must
 *    be `.nullable().optional()` (accepts missing OR `null` OR a real value, as three different
 *    outcomes), never bare `.optional()` (which only accepts missing or `undefined` - an explicit
 *    `null` 400s) and never `.nullable().optional().transform(v => v ?? undefined)` (collapses
 *    `null` into `undefined`, silently turning every "clear" request into a no-op - this is
 *    exactly the bug `updatePlanSchema` had for `tenantId`/`notes` before this fix; CREATE schemas
 *    may still collapse null->undefined via that transform since there's no prior value to
 *    preserve, but UPDATE schemas must not). The repository layer should pass the field through
 *    to `.set({...})` unchanged (drizzle only touches keys actually present in the object), which
 *    already does the right thing in this codebase without modification.
 *
 *  - Client: don't hand-roll which fields to send. Normalize the form's "current" state and the
 *    record's "original" (as-loaded) state the same way, so a field that's blank in both compares
 *    equal - e.g. a blank number input and a `capacity: null` record both become `null`, not `''`
 *    vs `null`. Then call `buildPatchPayload(original, current)`:
 *      - current equals original -> field omitted from the result (case 1 above).
 *      - current differs and is `null`/blank -> field sent as `null` (case 2, an explicit clear).
 *      - current differs and has a value -> field sent with that value (case 3).
 *    This generalizes past the common real-world shortcut of "always send null to mean clear,
 *    always omit to mean untouched" by also catching the one case that shortcut gets wrong: a
 *    field that was already blank and stays blank must still be omitted (it was never touched),
 *    not resent as a no-op `null` - `buildPatchPayload` enforces that by diffing against the
 *    original rather than only inspecting the current value.
 *
 * CREATE forms should keep using `omitNullish()`, not this - there is no "original" to diff
 * against for a brand-new record, so "blank" can only ever mean "omit", never "clear".
 */
export function buildPatchPayload<T extends Record<string, unknown>>(
  original: T,
  current: T,
): Partial<T> {
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(current)) {
    const curr = current[key];
    const orig = original[key];
    if (isSameValue(curr, orig)) continue;
    result[key] = curr;
  }
  return result as Partial<T>;
}

function isSameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => isSameValue(v, b[i]));
  }
  return false;
}
